import { useCurrentStateAndParams } from '@uirouter/react';
import { Code, History, AlertTriangle } from 'lucide-react';

import { PageHeader } from '@@/PageHeader';
import { Widget } from '@@/Widget/Widget';
import { WidgetBody } from '@@/Widget';
import { WidgetTabs, Tab, useCurrentTabIndex } from '@@/Widget/WidgetTabs';
import { Icon } from '@@/Icon';
import { Badge } from '@@/Badge';

import { YAMLInspector } from '../../components/YAMLInspector';
import { ResourceEventsDatatable } from '../../components/EventsDatatable/ResourceEventsDatatable';
import { useEventWarningsCount } from '../../queries/useEvents';

import { useCustomResource } from './queries/useCustomResource';

export function CustomResourceDetailsView() {
  const {
    params: { endpointId: environmentId, namespace, name, definition },
  } = useCurrentStateAndParams();

  const {
    data: yamlData,
    isLoading: isYamlLoading,
    isError: isYamlError,
  } = useCustomResource(environmentId, namespace, name, definition, {
    yaml: true,
  });

  // fetch JSON to read metadata.uid for events filtering
  const { data: crJson } = useCustomResource<{ metadata?: { uid?: string } }>(
    environmentId,
    namespace,
    name,
    definition,
    { yaml: false }
  );
  const resourceUid = crJson?.metadata?.uid;
  const eventWarningsCount = useEventWarningsCount(environmentId, {
    namespace,
    params: resourceUid ? { resourceId: resourceUid } : undefined,
  });

  const tabs: Tab[] = [
    {
      name: 'YAML',
      icon: Code,
      widget: (
        <div className="row">
          <div className="col-sm-12">
            <Widget>
              <WidgetBody>
                <YAMLInspector
                  identifier="customresource-yaml"
                  data={yamlData?.toString() || ''}
                  authorised
                  system={false}
                  hideMessage
                  isLoading={isYamlLoading}
                  isError={isYamlError}
                  data-cy="customresource-yaml"
                />
              </WidgetBody>
            </Widget>
          </div>
        </div>
      ),
      selectedTabParam: 'yaml',
    },
    {
      name: (
        <div className="flex items-center gap-x-2">
          Events
          {eventWarningsCount >= 1 && (
            <Badge type="warnSecondary">
              <Icon icon={AlertTriangle} className="!mr-1" />
              {eventWarningsCount}
            </Badge>
          )}
        </div>
      ),
      icon: History,
      widget: (
        <ResourceEventsDatatable
          storageKey="k8sCustomResourceDetail-eventsTable"
          namespace={namespace}
          resourceId={resourceUid}
          isLoading={!resourceUid}
          noWidget={false}
        />
      ),
      selectedTabParam: 'events',
    },
  ];

  const currentTabIndex = useCurrentTabIndex(tabs);

  if (!yamlData) {
    return null;
  }

  return (
    <>
      <PageHeader
        title="Custom Resource details"
        breadcrumbs={[
          {
            label: 'Custom Resources',
            link: 'kubernetes.moreResources.customResources',
          },
          name,
        ]}
        reload
      />
      <WidgetTabs tabs={tabs} currentTabIndex={currentTabIndex} />
      {tabs[currentTabIndex].widget}
    </>
  );
}
